import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, CalendarDays, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState } from '../components/Shared';

const TemplateCard = ({ t }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(t.body);
      setCopied(true);
      toast.success('Template copied. Paste it into your email and fill in the blanks.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy automatically. Please select and copy the text manually.');
    }
  };
  return (
    <Card className="h-full" data-testid="haveyoursay-template-card">
      <CardContent className="p-5 flex flex-col h-full">
        <h3 className="font-serif font-bold text-[#1a2744] leading-snug">{t.title}</h3>
        <pre className="mt-3 text-xs text-slate-600 leading-relaxed whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded-lg p-3 max-h-44 overflow-y-auto flex-1">{t.body}</pre>
        <Button onClick={copy} variant="outline" className="mt-3 h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press w-full" data-testid={`haveyoursay-copy-${t.id}`}>
          {copied ? <><Check className="h-4 w-4 mr-1.5 text-emerald-700" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy Template</>}
        </Button>
      </CardContent>
    </Card>
  );
};

export default function HowToHaveYourSay() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/have-your-say').then((r) => setContent(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!content) return <EmptyState title="Guide unavailable" subtitle="Please try again shortly." />;

  return (
    <div>
      <PageHeader kicker="RESIDENT POWER, STEP BY STEP" title="How to Have Your Say" subtitle="Attend. Watch. Speak. Follow up. A plain-language guide to being heard at City Hall." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <p className="text-base text-slate-700 leading-relaxed max-w-3xl" data-testid="haveyoursay-intro">{content.intro}</p>

        {/* Guide sections */}
        <div className="mt-8">
          <SectionHeader kicker="THE GUIDE" title="Every way to be heard" />
          <Card data-testid="haveyoursay-guide">
            <CardContent className="p-2 sm:p-4">
              <Accordion type="single" collapsible>
                {(content.sections || []).map((s) => (
                  <AccordionItem key={s.id} value={s.id}>
                    <AccordionTrigger className="text-left font-bold text-[#1a2744] text-sm sm:text-base px-2" data-testid={`haveyoursay-section-${s.id}`}>{s.title}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-700 px-2 leading-relaxed">{s.body}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Templates */}
        <div className="mt-10">
          <SectionHeader kicker="READY-TO-USE TEMPLATES" title="Copy, fill in, send" dek="Respectful, specific, and effective. Copy a template, fill in the square brackets, and send it." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="haveyoursay-templates">
            {(content.templates || []).map((t) => <TemplateCard key={t.id} t={t} />)}
          </div>
        </div>

        {/* CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10">
          <Card className="border-[#C9A84C]">
            <CardContent className="p-6">
              <CalendarDays className="h-7 w-7 text-[#1a2744] mb-2" />
              <h3 className="font-serif font-black text-[#1a2744]">See what Council is deciding</h3>
              <p className="text-sm text-slate-600 mt-1.5">Meeting dates, agendas, and how to participate.</p>
              <Button asChild className="mt-4 h-12 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="haveyoursay-meetings-cta"><Link to="/meetings">Council Meetings</Link></Button>
            </CardContent>
          </Card>
          <Card className="border-[#C9A84C]">
            <CardContent className="p-6">
              <FileWarning className="h-7 w-7 text-[#1a2744] mb-2" />
              <h3 className="font-serif font-black text-[#1a2744]">Document your concern first</h3>
              <p className="text-sm text-slate-600 mt-1.5">Get a tracking number so your issue is on the record.</p>
              <Button asChild className="mt-4 h-12 w-full bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="haveyoursay-report-cta"><Link to="/issue-tracker/report">Report an Issue</Link></Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
